import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import { DashboardLayout } from '@/components/ui/dashboard-layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { useCalendar, useUpdateTaskDate } from '@/hooks/use-calendar';
import { useTasks } from '@/hooks/use-tasks';
import { toast } from '@/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Eye,
  Calendar as CalendarIcon,
  List,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Milestone,
  Filter,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';

import 'react-big-calendar/lib/css/react-big-calendar.css';

// ── Localisation ────────────────────────────────────────────────
const localizer = momentLocalizer(moment);

// ── Drag-and-drop HOC ────────────────────────────────────────────
const DragAndDropCalendar = withDragAndDrop(Calendar);

// ── Helper: compute colour for an event based on type & status ──
const taskStatusColors: Record<string, { bg: string; border: string; text: string }> = {
  todo:     { bg: '#1e293b', border: '#475569', text: '#94a3b8' },
  in_progress: { bg: '#1e3a5f', border: '#3b82f6', text: '#60a5fa' },
  review:   { bg: '#3a2f1e', border: '#eab308', text: '#facc15' },
  done:     { bg: '#143d2b', border: '#10b981', text: '#34d399' },
};

const milestoneColor = { bg: '#2d1b69', border: '#8b5cf6', text: '#a78bfa' };

function getEventStyle(event: any) {
  if (event.type === 'milestone') {
    return {
      style: {
        backgroundColor: milestoneColor.bg,
        borderLeft: `4px solid ${milestoneColor.border}`,
        color: milestoneColor.text,
        borderRadius: '6px',
        fontSize: '12px',
        padding: '2px 6px',
        opacity: event.completed ? 0.6 : 1,
      },
    };
  }

  const colors = taskStatusColors[event.status] || taskStatusColors.todo;
  return {
    style: {
      backgroundColor: colors.bg,
      borderLeft: `4px solid ${colors.border}`,
      color: colors.text,
      borderRadius: '6px',
      fontSize: '12px',
      padding: '2px 6px',
      opacity: event.completed ? 0.6 : 1,
    },
  };
}

// ── Calendar Page ───────────────────────────────────────────────
export function CalendarPage() {
  useDocumentTitle('Calendar');

  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState<any>(Views.MONTH);
  const [projectFilter, setProjectFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Compute date range for the visible window
  const dateRange = useMemo(() => {
    const start = new Date(date);
    const end = new Date(date);

    if (view === Views.MONTH) {
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      start.setDate(start.getDate() - start.getDay()); // start of week containing 1st
      end.setMonth(end.getMonth() + 1, 0); // last day of month
      end.setHours(23, 59, 59, 999);
      end.setDate(end.getDate() + (6 - end.getDay())); // end of week containing last day
    } else if (view === Views.WEEK) {
      start.setDate(start.getDate() - start.getDay()); // Sunday
      start.setHours(0, 0, 0, 0);
      end.setDate(start.getDate() + 6); // Saturday
      end.setHours(23, 59, 59, 999);
    } else {
      // Day view
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    }

    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    };
  }, [date, view]);

  const { data, isLoading, error } = useCalendar(dateRange.start, dateRange.end);
  const updateTaskDate = useUpdateTaskDate();
  const { data: tasksData } = useTasks();
  const allTasks = tasksData?.data || [];

  const calendarData = data?.data;
  const tasks = calendarData?.tasks || [];
  const milestones = calendarData?.milestones || [];
  const projects = calendarData?.projects || [];

  // Build unique project list for filter
  const projectOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const t of [...tasks, ...milestones]) {
      if (!seen.has(t.projectId)) {
        seen.add(t.projectId);
        options.push({ value: t.projectId, label: t.projectName });
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [tasks, milestones]);

  // Total unfiltered count for "X of Y" display
  const totalUnfiltered = useMemo(() => {
    let count = 0;
    for (const t of tasks) if (t.dueDate) count++;
    for (const m of milestones) if (m.dueDate) count++;
    return count;
  }, [tasks, milestones]);

  // Transform data into calendar events
  const events = useMemo(() => {
    const result: any[] = [];

    for (const t of tasks) {
      if (!t.dueDate) continue;
      if (projectFilter && t.projectId !== projectFilter) continue;
      if (typeFilter === 'milestone') continue;

      const due = new Date(t.dueDate);
      const completed = t.status === 'done';
      result.push({
        id: `task-${t.id}`,
        title: t.title,
        start: due,
        end: due,
        allDay: true,
        resource: { ...t, type: 'task', completed },
        type: 'task',
        status: t.status,
        priority: t.priority,
        projectId: t.projectId,
        projectName: t.projectName,
        completed,
      });
    }

    for (const m of milestones) {
      if (!m.dueDate) continue;
      if (projectFilter && m.projectId !== projectFilter) continue;
      if (typeFilter === 'task') continue;

      const due = new Date(m.dueDate);
      const completed = !!m.completedAt;
      result.push({
        id: `milestone-${m.id}`,
        title: `◆ ${m.name}`,
        start: due,
        end: due,
        allDay: true,
        resource: { ...m, type: 'milestone', completed },
        type: 'milestone',
        status: completed ? 'done' : 'pending',
        completed,
      });
    }

    return result;
  }, [tasks, milestones, projectFilter, typeFilter]);

  // Handle event click — navigate to the resource
  const handleSelectEvent = useCallback(
    (event: any) => {
      if (event.type === 'milestone') {
        navigate(`/projects/${event.resource.projectId}`);
      } else {
        // Tasks are viewed on the kanban board within their project
        navigate(`/projects/${event.resource.projectId}?tab=kanban&taskId=${event.resource.id}`);
      }
    },
    [navigate]
  );

  // Handle drag-to-reschedule
  const handleEventDrop = useCallback(
    async ({ event, start, end }: { event: any; start: Date; end: Date }) => {
      if (event.type !== 'task') return; // only tasks can be rescheduled

      const newDueDate = start.toISOString().split('T')[0];
      const taskId = event.resource.id;

      try {
        await updateTaskDate.mutateAsync({ id: taskId, dueDate: newDueDate });
        toast({
          title: 'Task rescheduled',
          description: `"${event.title}" moved to ${newDueDate}`,
          variant: 'success',
        });
      } catch (err: any) {
        toast({
          title: 'Failed to reschedule',
          description: err?.message || 'An unexpected error occurred.',
          variant: 'destructive',
        });
      }
    },
    [updateTaskDate]
  );

  // Navigate handlers
  const handleNavigate = useCallback((newDate: Date) => setDate(newDate), []);
  const handleViewChange = useCallback((newView: string) => setView(newView), []);

  // Custom event component
  const EventComponent = useCallback(({ event }: { event: any }) => {
    return (
      <div className="truncate font-medium leading-tight">
        {event.title}
      </div>
    );
  }, []);

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Calendar</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Visual timeline of tasks, milestones, and project deadlines.
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5 text-sm text-[var(--text-secondary)]">
              <Filter size={14} />
              <span>Filters:</span>
            </div>

            {/* Project filter */}
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-[200px] h-8 text-xs" aria-label="Filter by project">
                <SelectValue placeholder="All projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-projects">All projects</SelectItem>
                {projectOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Type filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[160px] h-8 text-xs" aria-label="Filter by type">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-types">All types</SelectItem>
                <SelectItem value="task">Tasks only</SelectItem>
                <SelectItem value="milestone">Milestones only</SelectItem>
              </SelectContent>
            </Select>

            {/* Clear filters */}
            {(projectFilter || typeFilter) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setProjectFilter(''); setTypeFilter(''); }}
                className="h-8 text-xs gap-1"
              >
                <X size={12} />
                Clear
              </Button>
            )}

            {/* Spacer */}
            <div className="flex-1" />

            {/* Legend */}
            <div className="flex items-center gap-4 text-xs text-[var(--text-secondary)]">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: taskStatusColors.todo.border }} />
                Task
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: milestoneColor.border }} />
                Milestone
              </span>
              {events.length > 0 && (
                <span className="text-[var(--text-secondary)]">
                  {projectFilter || typeFilter
                    ? `${events.length} of ${totalUnfiltered} event${totalUnfiltered !== 1 ? 's' : ''}`
                    : `${events.length} event${events.length !== 1 ? 's' : ''}`}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error state */}
      {error ? (
        <Card>
          <CardContent className="p-12 text-center">
            <AlertCircle size={32} className="mx-auto mb-3 text-red-400" />
            <p className="text-red-400 mb-1">Failed to load calendar data.</p>
            <p className="text-sm text-[var(--text-secondary)]">
              Please check your connection and try again.
            </p>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-3">
              {/* Simulate header */}
              <div className="flex items-center justify-between mb-4">
                <Skeleton className="h-10 w-48" />
                <div className="flex gap-2">
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-8 w-20" />
                </div>
              </div>
              {/* Simulate grid rows */}
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex gap-1">
                  {Array.from({ length: 7 }).map((_, j) => (
                    <Skeleton key={j} className="h-20 flex-1" />
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 sm:p-2">
            {events.length === 0 && !isLoading && !error ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <CalendarIcon size={48} className="mx-auto mb-4 text-[var(--text-secondary)]" />
                <p className="text-[var(--text-secondary)] mb-1">No events to display</p>
                <p className="text-sm text-[var(--text-secondary)]">
                  {projectFilter || typeFilter ? 'Try adjusting your filters.' : 'Tasks and milestones with due dates will appear here.'}
                </p>
                {(projectFilter || typeFilter) && (
                  <Button variant="ghost" size="sm" onClick={() => { setProjectFilter(''); setTypeFilter(''); }} className="mt-3 text-xs">
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : (
            <div className="calendar-container" style={{ height: view === Views.DAY ? 600 : 700 }}>
              <DragAndDropCalendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '100%' }}
                views={[Views.MONTH, Views.WEEK, Views.DAY]}
                defaultView={Views.MONTH}
                view={view}
                date={date}
                onNavigate={handleNavigate}
                onView={handleViewChange}
                onSelectEvent={handleSelectEvent}
                onEventDrop={handleEventDrop}
                draggableAccessor={(event: any) => event.type === 'task'}
                eventPropGetter={getEventStyle}
                components={{ event: EventComponent }}
                popup
                selectable
                className="rbc-custom"
              />
            </div>
            )}
          </CardContent>
        </Card>
      )}
    </DashboardLayout>
  );
}
